import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { permitteesService } from "../../../services/permitteesService";
import {
    Box,
    Button,
    Divider,
    Flex,
    Grid,
    GridItem,
    Stack,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Text,
    useColorModeValue,
} from "@chakra-ui/react";
import Card from "../../../components/Card/Card";
import CardHeader from "../../../components/Card/CardHeader";
import CardBody from "../../../components/Card/CardBody";
import Loading from "../../../components/Loading/Loading";
import TimelineRow from "../../../components/Tables/TimelineRow";
import { MdCreditCard, MdEdit, MdEvent, MdNoteAdd, MdPermIdentity, MdPersonAdd, MdPrint, MdSend } from "react-icons/md";

const iconMapPermissionario = {
    "permissionario-created": MdPersonAdd,
    "permissionario-edited": MdEdit,
    "document-created": MdNoteAdd,
    "document-validity-end": MdEvent,
    "document-printed": MdPrint,
    "license-printed": MdPermIdentity,
    "document-payment": MdCreditCard,
    "document-sent-to-detran": MdSend,
};

export const PermitteesDetail = ({ initialData = null }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const textColor = useColorModeValue("gray.700", "white");

    useEffect(() => {
        if (id || initialData) fetchData();
    }, [id, initialData]);

    const fetchData = async () => {
        try {
            const payload = initialData || (await permitteesService.detail(id));
            setData(payload);
        } catch (err) {
            console.error(t("permittees.messages.errors.find"), err);
        }
    };

    return (
        <Flex w="100%" direction="column" pt={{ sm: "125px", lg: "75px" }}>
            <Card px="0" mb="24px">
                <CardHeader px="22px" mb="24px">
                    <Flex direction="column">
                        <Text color={textColor} fontSize="lg" fontWeight="bold" mb="6px">
                            {t("permittees.details.title")}
                        </Text>
                    </Flex>
                </CardHeader>
                <CardBody px="22px">
                    {data ? (
                        <Tabs variant="enclosed">
                            <TabList overflowX="auto" whiteSpace="nowrap" sx={{ "&::-webkit-scrollbar": { display: "none" }}}>
                                <Tab>{t("permittees.details.tab-general.title")}</Tab>
                                <Tab>{t("permittees.details.tab-vehicle.title")}</Tab>
                                <Tab>{t("permittees.details.tab-owner.title")}</Tab>
                                <Tab>{t("permittees.details.tab-defender.title")}</Tab>
                                {data.images?.length > 0 && <Tab>{t("permittees.details.tab-photo.title")}</Tab>}
                                <Tab>{t("permittees.details.tab-timeline.title")}</Tab>
                            </TabList>

                            <TabPanels>
                                {/* Geral */}
                                <TabPanel>
                                    <Card border="1px solid #3884cc" mb="30px">
                                        <CardHeader>
                                            <Text color={textColor} fontSize="md" fontWeight="bold">
                                                {t("permittees.details.tab-general.permittees")}
                                            </Text>
                                        </CardHeader>
                                        <Divider borderColor="#3884cc" mb="20px" />
                                        <CardBody>
                                            <Stack spacing={4}>
                                                <Grid gap="6px 0" templateColumns={{ base: "1fr", md: "repeat(3,1fr)" }}>
                                                    <GridItem>
                                                        <Text as="span" fontWeight="bold">{t("permittees.details.tab-general.location")}:{"\u00A0"}</Text>{data.location.description}
                                                    </GridItem>
                                                    <GridItem>
                                                        <Text as="span" fontWeight="bold">{t("permittees.details.tab-general.number")}:{"\u00A0"}</Text>{data.numero}
                                                    </GridItem>
                                                    <GridItem>
                                                        <Text as="span" fontWeight="bold">{t("permittees.details.tab-general.inscription")}:{"\u00A0"}</Text>{data.inscricao_municipal}
                                                    </GridItem>
                                                    <GridItem>
                                                        <Text as="span" fontWeight="bold">{t("permittees.details.tab-general.creator")}:{"\u00A0"}</Text>{data.creator.name}
                                                    </GridItem>
                                                    <GridItem>
                                                        <Text as="span" fontWeight="bold">{t("permittees.details.tab-general.observations")}:{"\u00A0"}</Text>{data.observacoes}
                                                    </GridItem>
                                                    <GridItem>
                                                        <Text as="span" fontWeight="bold">{t("permittees.details.tab-general.start-date")}:{"\u00A0"}</Text>{data.data_inicio_atividade}
                                                    </GridItem>
                                                </Grid>
                                            </Stack>
                                        </CardBody>
                                    </Card>
                                </TabPanel>

                                {/* Veículo */}
                                <TabPanel>
                                    <Card border="1px solid #3884cc" mb="30px">
                                        <CardHeader>
                                            <Text color={textColor} fontSize="md" fontWeight="bold">
                                                {t("permittees.details.tab-vehicle.title")}
                                            </Text>
                                        </CardHeader>
                                        <Divider borderColor="#3884cc" mb="20px" />
                                        <CardBody>
                                            <Stack spacing={6}>
                                                <Grid gap="6px 0" templateColumns={{ base: "1fr", md: "repeat(3,1fr)" }}>
                                                <GridItem>
                                                    <Text as="span" fontWeight="bold">{t("permittees.details.tab-vehicle.plate")}:{"\u00A0"}</Text>{data.veiculo.placa}
                                                    <Text as="span" fontWeight="bold" ml="4px">{t("permittees.details.tab-vehicle.uf-plate")}:{"\u00A0"}</Text>{data.veiculo.uf_placa}
                                                </GridItem>
                                                    <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-vehicle.brand-model")}:{"\u00A0"}</Text>{data.veiculo.descricao_marca_modelo}</GridItem>
                                                    <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-vehicle.chassis")}:{"\u00A0"}</Text>{data.veiculo.numero_chassi}</GridItem>
                                                    <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-vehicle.year-fabric")}:{"\u00A0"}</Text> {data.veiculo.ano_fabricacao}<Text as="span" fontWeight="bold" ml="4px">{t("permittees.details.tab-vehicle.year-model")}:{"\u00A0"}</Text>{data.veiculo.ano_modelo}</GridItem>
                                                    <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-vehicle.color")}:{"\u00A0"}</Text>{data.veiculo.descricao_cor}</GridItem>
                                                    <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-vehicle.renavam")}:{"\u00A0"}</Text>{data.veiculo.numero_renavam}</GridItem>
                                                {data.cab && (
                                                        <>
                                                            <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-vehicle.taximeter-number")}:{"\u00A0"}</Text>{data.veiculo.numero_taximetro}</GridItem>
                                                            <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-vehicle.taximeter-model")}:{"\u00A0"}</Text>{data.veiculo.modelo_taximetro}</GridItem>
                                                            <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-vehicle.taximeter-brand")}:{"\u00A0"}</Text>{data.veiculo.marca_taximetro}</GridItem>
                                                        </>
                                                )}
                                                </Grid>
                                            </Stack>
                                        </CardBody>
                                    </Card>
                                </TabPanel>

                                {/* Proprietário */}
                                <TabPanel>
                                    <Card border="1px solid #3884cc" mb="30px">
                                        <CardHeader>
                                            <Text color={textColor} fontSize="md" fontWeight="bold">
                                                {t("permittees.details.tab-owner.title")}
                                            </Text>
                                        </CardHeader>
                                        <Divider borderColor="#3884cc" mb="20px" />
                                        <CardBody>
                                            <Stack spacing={6}>
                                                <Grid gap="6px 0" templateColumns={{ base: "1fr", md: "repeat(3,1fr)" }}>
                                                    <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.name")}:{"\u00A0"}</Text>{data.proprietario.nome}</GridItem>
                                                    <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.birth-date")}:{"\u00A0"}</Text>{data.proprietario.data_nascimento}</GridItem>
                                                    <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.cpf-cnpj")}:{"\u00A0"}</Text>{data.proprietario.cpf_cnpj}</GridItem>
                                                    <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.nickname")}:{"\u00A0"}</Text>{data.proprietario.apelido}</GridItem>
                                                    <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.identity")}:{"\u00A0"}</Text>{data.proprietario.identidade}</GridItem>
                                                    <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.issuing-agency")}:{"\u00A0"}</Text>{data.proprietario.orgao_expedidor}<Text as="span" fontWeight="bold" ml="4px">{t("permittees.details.tab-owner.identity-uf")}:{"\u00A0"}</Text>{data.proprietario.uf_identidade}</GridItem>
                                                </Grid>
                                            </Stack>
                                        </CardBody>
                                    </Card>
                                    <Card border="1px solid #3884cc" mb="30px">
                                        <CardHeader>
                                            <Text color={textColor} fontSize="md" fontWeight="bold">
                                                {t("permittees.details.tab-owner.cnh")}
                                            </Text>
                                        </CardHeader>
                                        <Divider borderColor="#3884cc" mb="20px" />
                                        <CardBody>
                                            <Stack spacing={6}>
                                                <Grid gap="6px 0" templateColumns={{ base: "1fr", md: "repeat(3,1fr)" }}>
                                                    <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.cnh")}:{"\u00A0"}</Text>{data.proprietario.cnh}</GridItem>
                                                    <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.cnh-category")}:{"\u00A0"}</Text>{data.proprietario.categoria_cnh}</GridItem>
                                                    <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.cnh-validity")}:{"\u00A0"}</Text>{data.proprietario.validade_cnh}</GridItem>
                                                </Grid>
                                            </Stack>
                                        </CardBody>
                                    </Card>
                                    <Card border="1px solid #3884cc" mb="30px">
                                        <CardHeader>
                                            <Text color={textColor} fontSize="md" fontWeight="bold">
                                                {t("permittees.details.tab-owner.address-section")}
                                            </Text>
                                        </CardHeader>
                                        <Divider borderColor="#3884cc" mb="20px" />
                                        <CardBody>
                                            <Stack spacing={6}>
                                                <Grid gap="6px 0" templateColumns={{ base: "1fr", md: "repeat(3,1fr)" }}>
                                                    <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.cep")}:{"\u00A0"}</Text>{data.proprietario.cep}</GridItem>
                                                    <GridItem colSpan={{ base: 1, md: 2 }}><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.address")}:{"\u00A0"}</Text>{data.proprietario.logradouro}</GridItem>
                                                    <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.address-number")}:{"\u00A0"}</Text>{data.proprietario.numero}</GridItem>
                                                    <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.neighborhood")}:{"\u00A0"}</Text> {data.proprietario.bairro}</GridItem>
                                                    <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.city")}:{"\u00A0"}</Text>{data.proprietario.municipio}<Text as="span" fontWeight="bold" ml="4px">{t("permittees.details.tab-owner.uf")}:{"\u00A0"}</Text>{data.proprietario.uf}</GridItem>
                                                </Grid>
                                            </Stack>
                                        </CardBody>
                                    </Card>
                                    <Card border="1px solid #3884cc" mb="30px">
                                        <CardHeader>
                                            <Text color={textColor} fontSize="md" fontWeight="bold">
                                                {t("permittees.details.tab-owner.phone-section")}
                                            </Text>
                                        </CardHeader>
                                        <Divider borderColor="#3884cc" mb="20px" />
                                        <CardBody>
                                            <Stack spacing={6}>
                                                <Grid gap="6px 0" templateColumns={{ base: "1fr", md: "repeat(2,1fr)" }}>
                                                    <GridItem><Text color={textColor} fontSize="md"><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.phone-landline")}:{"\u00A0"}</Text>{data.proprietario.telefone_fixo}</Text></GridItem>
                                                    <GridItem><Text color={textColor} fontSize="md"><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.phone-mobile")}:{"\u00A0"}</Text>{data.proprietario.telefone_celular}</Text></GridItem>
                                                </Grid>
                                            </Stack>
                                        </CardBody>
                                    </Card>
                                </TabPanel>

                                {/* Defensor */}
                                <TabPanel>
                                    <Card border="1px solid #3884cc" mb="30px">
                                        <CardHeader>
                                            <Text color={textColor} fontSize="md" fontWeight="bold">
                                                {t("permittees.details.tab-defender.title")}
                                            </Text>
                                        </CardHeader>
                                        <Divider borderColor="#3884cc" mb="20px" />
                                        <CardBody>
                                            <Stack spacing={6}>
                                                <Grid gap="6px 0" templateColumns={{ base: "1fr", md: "repeat(3,1fr)" }}>
                                                    <GridItem><Text color={textColor} fontSize="md"><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.name")}:{"\u00A0"}</Text>{data.defensor.nome}</Text></GridItem>                                                    
                                                    <GridItem><Text color={textColor} fontSize="md"><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.birth-date")}:{"\u00A0"}</Text>{data.defensor.data_nascimento}</Text></GridItem>
                                                    <GridItem><Text color={textColor} fontSize="md"><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.cpf-cnpj")}:{"\u00A0"}</Text>{data.defensor.cpf_cnpj}</Text></GridItem>
                                                    <GridItem><Text color={textColor} fontSize="md"><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.nickname")}:{"\u00A0"}</Text>{data.defensor.apelido}</Text></GridItem>
                                                    <GridItem><Text color={textColor} fontSize="md"><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.identity")}:{"\u00A0"}</Text>{data.defensor.identidade}</Text></GridItem>
                                                    <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.issuing-agency")}:{"\u00A0"}</Text>{data.defensor.orgao_expedidor}<Text as="span" fontWeight="bold" ml="4px">{t("permittees.details.tab-owner.identity-uf")}:{"\u00A0"}</Text>{data.defensor.uf_identidade}</GridItem>
                                                </Grid>
                                            </Stack>
                                        </CardBody>
                                    </Card>
                                    <Card border="1px solid #3884cc" mb="30px">
                                        <CardHeader>
                                            <Text color={textColor} fontSize="md" fontWeight="bold">
                                                {t("permittees.details.tab-owner.cnh")}
                                            </Text>
                                        </CardHeader>
                                        <Divider borderColor="#3884cc" mb="20px" />
                                        <CardBody>
                                            <Stack spacing={6}>
                                                <Grid gap="6px 0" templateColumns={{ base: "1fr", md: "repeat(3,1fr)" }}>
                                                    <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.cnh")}:{"\u00A0"}</Text>{data.defensor.cnh}</GridItem>
                                                    <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.cnh-category")}:{"\u00A0"}</Text>{data.defensor.categoria_cnh}</GridItem>
                                                    <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.cnh-validity")}:{"\u00A0"}</Text>{data.defensor.validade_cnh}</GridItem>
                                                </Grid>
                                            </Stack>
                                        </CardBody>
                                    </Card>
                                    <Card border="1px solid #3884cc" mb="30px">
                                        <CardHeader>
                                            <Text color={textColor} fontSize="md" fontWeight="bold">
                                                {t("permittees.details.tab-owner.address-section")}
                                            </Text>
                                        </CardHeader>
                                        <Divider borderColor="#3884cc" mb="20px" />
                                        <CardBody>
                                            <Stack spacing={6}>
                                                <Grid gap="6px 0" templateColumns={{ base: "1fr", md: "repeat(3,1fr)" }}>
                                                    <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.cep")}:{"\u00A0"}</Text>{data.defensor.cep}</GridItem>
                                                    <GridItem colSpan={{ base: 1, md: 2 }}><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.address")}:{"\u00A0"}</Text>{data.defensor.logradouro}</GridItem>
                                                    <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.address-number")}:{"\u00A0"}</Text>{data.defensor.numero}</GridItem>
                                                    <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.neighborhood")}:{"\u00A0"}</Text> {data.defensor.bairro}</GridItem>
                                                    <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.city")}:{"\u00A0"}</Text>{data.defensor.municipio}<Text as="span" fontWeight="bold" ml="4px">{t("permittees.details.tab-owner.uf")}:{"\u00A0"}</Text>{data.defensor.uf}</GridItem>
                                                </Grid>
                                            </Stack>
                                        </CardBody>
                                    </Card>
                                    <Card border="1px solid #3884cc" mb="30px">
                                        <CardHeader>
                                            <Text color={textColor} fontSize="md" fontWeight="bold">
                                                {t("permittees.details.tab-owner.phone-section")}
                                            </Text>
                                        </CardHeader>
                                        <Divider borderColor="#3884cc" mb="20px" />
                                        <CardBody>
                                            <Stack spacing={6}>
                                                <Grid gap="6px 0" templateColumns={{ base: "1fr", md: "repeat(2,1fr)" }}>
                                                    <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.phone-landline")}:{"\u00A0"}</Text>{data.defensor.telefone_fixo}</GridItem>
                                                    <GridItem><Text as="span" fontWeight="bold">{t("permittees.details.tab-owner.phone-mobile")}:{"\u00A0"}</Text>{data.defensor.telefone_celular}</GridItem>
                                                </Grid>
                                            </Stack>
                                        </CardBody>
                                    </Card>
                                </TabPanel>

                                {/* Foto */}
                                {data.images?.length > 0 && (
                                    <TabPanel>
                                        <Card border="1px solid #3884cc" mb="30px">
                                            <CardHeader>
                                                <Text color={textColor} fontSize="md" fontWeight="bold">
                                                    {t("permittees.details.tab-photo.title")}
                                                </Text>
                                            </CardHeader>
                                            <CardBody>
                                                <Grid gap="10px" templateColumns={{ base: "1fr", md: "repeat(2,1fr)" }}>
                                                    {data.images.map((src, idx) => (
                                                        <Box key={idx}>
                                                            <img src={src} alt={`Foto ${idx + 1}`} style={{ width: "100%" }} />
                                                        </Box>
                                                    ))}
                                                </Grid>
                                            </CardBody>
                                        </Card>
                                    </TabPanel>
                                )}

                                {/* Linha do Tempo */}
                                <TabPanel>
                                    <Card border="1px solid #3884cc" mb="30px">
                                        <Text fontSize="md" fontWeight="bold" color={textColor}>
                                            {t("permittees.details.tab-timeline.title")}
                                        </Text>
                                        <Box m="4">
                                            {data.timeline.length > 0 && (
                                                <Stack>
                                                    {data.timeline.map((entry, index) => {
                                                        return (
                                                            <TimelineRow
                                                                key={index}
                                                                logo={iconMapPermissionario[entry.icon]}
                                                                title={entry.description}
                                                                titleColor={textColor}
                                                                date={entry.date}
                                                                index={index + 1}
                                                                arrLength={data.timeline.length + 1}
                                                                isDark={false}
                                                            />
                                                        );
                                                    })}
                                                </Stack>
                                            )}
                                        </Box>
                                    </Card>
                                </TabPanel>
                            </TabPanels>
                        </Tabs>
                    ) : (
                        <Loading maxH={{ sm: "100px", md: "350px" }} />
                    )}
                </CardBody>
                <CardBody px="22px">
                    <Flex justify="flex-start">
                        <Button mt="24px" w="125px" h="40px" onClick={() => navigate(-1)}>
                            {t("system.actions.back")}
                        </Button>
                    </Flex>
                </CardBody>
            </Card>
        </Flex>
    );
};

export default PermitteesDetail;
